import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import de from './locales/de.json'
import en from './locales/en.json'

export const DEFAULT_LANGUAGE = 'de'
export const LANGUAGE_STORAGE_KEY = 'language'

// The only locales the brand surface supports (the BE rejects anything else with
// `400 parameters_invalid`). Shared by every language picker — the profile
// language setting and the team-invite locale field.
export const SUPPORTED_LANGUAGES: readonly { code: BrandLocale; labelKey: string }[] = [
  { code: 'de', labelKey: 'settings.language.de' },
  { code: 'en', labelKey: 'settings.language.en' },
]

export type BrandLocale = 'de' | 'en'

/** Narrows an arbitrary i18next language tag to a locale the BE accepts. */
export function toBrandLocale(language: string): BrandLocale {
  // Region-tagged tags count too (`en-US`, `en_GB`): the locale on the user
  // record is not guaranteed to be a bare language code.
  return language.toLowerCase().split(/[-_]/)[0] === 'en' ? 'en' : 'de'
}

// The initial language must be deterministic so the server and the client's
// first render produce identical HTML (otherwise React reports a hydration
// mismatch). We do NOT detect the language during init — the server has no
// access to localStorage/navigator and would diverge from the client. Instead
// we start from the fallback and restore the persisted language on the client
// after hydration (see restoreLanguage / RootDocument).
void i18n.use(initReactI18next).init({
  resources: {
    de: { translation: de },
    en: { translation: en },
  },
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  // Initialise synchronously. Resources are inlined (no backend), so there is
  // nothing to load asynchronously — this makes `isInitialized` true and the
  // store ready by the time init() returns. With the default (async) init the
  // server and the client's first render could see different init states,
  // producing an intermittent hydration mismatch on first paint.
  initAsync: false,
  react: {
    // Never suspend for translation loading: everything is ready synchronously,
    // and a suspended subtree during SSR would diverge from the client's first
    // render (the other half of the same hydration mismatch).
    useSuspense: false,
  },
})

export function getStoredLanguage(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function persistLanguage(language: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Ignore write failures (private mode, storage disabled, etc.)
  }
}

/**
 * Applies the locale carried on the user record (GET /me). The server value is
 * authoritative and overwrites the persisted one: localStorage only holds the
 * *last* session's language, so without this a second account with a different
 * locale would keep speaking the first account's language.
 *
 * Called on every /me load (bootstrap, login, refresh, ToS acceptance), so it
 * also picks up a locale changed from another device.
 */
export function applyUserLanguage(locale: string | undefined): void {
  if (!locale) return
  const language = toBrandLocale(locale)
  persistLanguage(language)
  if (language !== i18n.language) {
    void i18n.changeLanguage(language)
  }
}

/**
 * Applies the persisted language. Call from a client-only effect after
 * hydration so it never runs during SSR or the initial client render.
 *
 * This is the pre-auth guess only — once /me resolves, applyUserLanguage wins.
 */
export function restoreLanguage(): void {
  const stored = getStoredLanguage()
  if (stored && stored !== i18n.language) {
    void i18n.changeLanguage(stored)
  }
}

export default i18n
